import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { OpeningOverlay } from "~/components/lock/MyLocks/OpeningOverlay";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { endRentalPageQuery } from "./__generated__/endRentalPageQuery.graphql";
import type { endRentalPageDeactivateMutation } from "./__generated__/endRentalPageDeactivateMutation.graphql";
import type { endRentalPageEndB2CMutation } from "./__generated__/endRentalPageEndB2CMutation.graphql";
import type { endRentalPageOpenMutation } from "./__generated__/endRentalPageOpenMutation.graphql";
import type { endRentalPageOpenOwnerMutation } from "./__generated__/endRentalPageOpenOwnerMutation.graphql";

export const query = graphql`
	query endRentalPageQuery($reservationId: UUID!) @relay_test_operation {
		reservation(id: $reservationId) {
			... on LockReservation {
				id
				subtype {
					__typename
				}
				space {
					id
					frame
					position
				}
				utilizationGrants(filter: { active: true }, pagination: { page: 0, pageSize: 1000 }) @catch(to: NULL) {
					nodes {
						id
						permissions
					}
				}
			}
		}
	}
`;

// B2B has no billing step, so confirming goes straight to the ended summary.
const deactivateMutation = graphql`
	mutation endRentalPageDeactivateMutation($id: UUID!) {
		b2BLockReservationDeactivate(id: $id)
	}
`;

const endB2CMutation = graphql`
	mutation endRentalPageEndB2CMutation($input: B2ClockSessionEndInput!) {
		b2CLockSessionEnd(input: $input) {
			id
			status
			paymentStatus
			finalAmount
		}
	}
`;

const openMutation = graphql`
	mutation endRentalPageOpenMutation($id: UUID!) {
		utilizationGrantUse(id: $id) {
			id
			unlockRequest {
				id
			}
		}
	}
`;

const openOwnerMutation = graphql`
	mutation endRentalPageOpenOwnerMutation($reservationId: UUID!) {
		lockReservationUnlock(input: { lockReservationId: $reservationId }) {
			id
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/end-rental/$reservationId")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ source: z.enum(["b2c", "owner"]).optional() })),
	loader: async (ctx) => {
		const source = new URLSearchParams(ctx.location.searchStr).get("source");
		const sessionExists = await checkSession();
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/my${ctx.location.searchStr}` },
			});
		}
		return preloadQuery<endRentalPageQuery>(ctx.context.relayEnvironment, query, {
			reservationId: ctx.params.reservationId,
		});
	},
});

function RouteComponent() {
	const { hubMetaName, reservationId } = Route.useParams();
	const { source } = Route.useSearch();
	return (
		<EndRentalPage
			queryRef={Route.useLoaderData()}
			hubMetaName={hubMetaName}
			reservationId={reservationId}
			source={source}
			service="LOCK_B2B"
		/>
	);
}

export function EndRentalPage(props: {
	queryRef: PreloadedQuery<endRentalPageQuery>;
	hubMetaName: string;
	reservationId: string;
	source?: "b2c" | "owner";
	service: "LOCK_B2B" | "LOCK_B2C";
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { hubMetaName, reservationId, source, service } = props;
	const { reservation } = usePreloadedQuery<endRentalPageQuery>(query, props.queryRef);
	const [deactivate, isEnding] = useAsyncMutation<endRentalPageDeactivateMutation>(deactivateMutation);
	const [endB2C, isEndingB2C] = useAsyncMutation<endRentalPageEndB2CMutation>(endB2CMutation);
	const [openGrant, isOpening] = useAsyncMutation<endRentalPageOpenMutation>(openMutation);
	const [openOwner, isOpeningOwner] = useAsyncMutation<endRentalPageOpenOwnerMutation>(openOwnerMutation);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [unlockRequestId, setUnlockRequestId] = useState<string | undefined>();

	const grants = reservation?.utilizationGrants?.nodes ?? [];
	const openableGrant = grants.find((g) => (g.permissions ?? []).includes("UNLOCK"));
	const subtype = reservation?.subtype;
	const isB2C = source === "b2c" || subtype?.__typename === "B2ClockReservationSubtype";
	const ending = isEnding || isEndingB2C;

	const goBack = () =>
		service === "LOCK_B2C"
			? navigate({ to: "/$hubMetaName/lock", params: { hubMetaName } })
			: navigate({ to: "/$hubMetaName/swap/my", params: { hubMetaName } });

	const handleOpen = async () => {
		try {
			const requestId =
				source === "b2c" || source === "owner"
					? (await openOwner({ variables: { reservationId } })).lockReservationUnlock.id
					: openableGrant
						? (await openGrant({ variables: { id: openableGrant.id } })).utilizationGrantUse.unlockRequest?.id
						: undefined;
			if (!requestId) throw new Error("Opening the lock did not return an unlock request");
			setUnlockRequestId(requestId);
		} catch (error) {
			console.error(error);
			toast.error(i18n.SOMETHING_WENT_WRONG());
		}
	};

	if (unlockRequestId) {
		return (
			<OpeningOverlay
				service={service}
				unlockRequestId={unlockRequestId}
				onTerminate={(outcome) => {
					if (outcome === "success") toast.success(i18n.LOCK_OPENED_RECENTLY());
					setUnlockRequestId(undefined);
				}}
			/>
		);
	}

	const handleConfirm = async () => {
		try {
			if (isB2C) {
				await endB2C({ variables: { input: { b2CSessionId: reservationId } } });
			} else {
				await deactivate({ variables: { id: reservationId } });
			}
			setIsConfirmOpen(false);
			if (service === "LOCK_B2C") {
				navigate({
					to: "/$hubMetaName/lock/ended/$reservationId",
					params: { hubMetaName, reservationId },
					search: { source: "b2c" },
					replace: true,
				});
			} else {
				navigate({
					to: "/$hubMetaName/swap/ended/$reservationId",
					params: { hubMetaName, reservationId },
					search: source === "b2c" ? { source: "b2c" } : {},
					replace: true,
				});
			}
		} catch (error) {
			console.error(error);
			toast.error(i18n.SOMETHING_WENT_WRONG());
		}
	};

	return (
		<PageContainer
			footer={
				<div className="flex flex-col gap-3">
					<Button size="lg" className="w-full shadow-xl" disabled={ending} onClick={() => setIsConfirmOpen(true)}>
						<T>{i18n.LOCK_END_RENTAL_CONTINUE()}</T>
					</Button>
					<Button size="lg" variant="outline" className="w-full shadow-xl" disabled={ending} onClick={goBack}>
						<T>{i18n.LOCK_END_RENTAL_BACK()}</T>
					</Button>
				</div>
			}
		>
			<AppHeader service={service} />

			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{service === "LOCK_B2C" ? i18n.LOCK_END_RENTAL() : i18n.LOCK_END_USAGE()}</T>
				</h1>

				<div className="flex flex-col gap-4">
					<div className="flex items-start gap-4 border-t pt-5">
						<TriangleAlert className="size-6 shrink-0 text-foreground" />
						<div className="flex flex-col gap-1">
							<p className="text-base font-breit font-medium text-foreground">
								<T>{i18n.LOCK_END_RENTAL_WARNING_TITLE()}</T>
							</p>
							<p className="text-sm text-muted-foreground">
								<T>{i18n.LOCK_END_RENTAL_WARNING_DESC()}</T>
							</p>
						</div>
					</div>

					{source === "b2c" || source === "owner" || openableGrant ? (
						<Button variant="outline" className="w-full" disabled={isOpening || isOpeningOwner} onClick={() => void handleOpen()}>
							<T>{i18n.LOCK_END_RENTAL_OPEN_COMPARTMENT()}</T>
						</Button>
					) : null}
				</div>
			</div>

			{/* Step 2: irreversibility confirmation drawer */}
			<Drawer open={isConfirmOpen} onOpenChange={(open) => !open && setIsConfirmOpen(false)}>
				<DrawerContent>
					<DrawerHeader className="flex-row items-start justify-between gap-4 text-left">
						<div className="flex flex-col gap-1">
							<DrawerTitle className="text-2xl font-breit font-medium">
								<T>{i18n.LOCK_END_RENTAL_CONFIRM_TITLE()}</T>
							</DrawerTitle>
							<DrawerDescription className="text-sm text-muted-foreground">
								<T>{i18n.LOCK_END_RENTAL_CONFIRM_DESC()}</T>
							</DrawerDescription>
						</div>
						<DrawerClose asChild>
							<Button variant="ghost" size="icon" className="shrink-0" disabled={ending}>
								<X className="size-5" />
							</Button>
						</DrawerClose>
					</DrawerHeader>

					<DrawerFooter className="border-t">
						<Button
							variant="destructive"
							className="w-full shadow-xs"
							disabled={ending}
							onClick={(e) => {
								e.preventDefault();
								void handleConfirm();
							}}
						>
							<T>{i18n.LOCK_END_RENTAL_CONFIRM_CTA()}</T>
						</Button>
						<DrawerClose asChild>
							<Button variant="outline" className="w-full" disabled={ending}>
								<T>{i18n.LOCK_END_RENTAL_CANCEL()}</T>
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>
		</PageContainer>
	);
}
